import React, { useState } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogBody,
    DialogContent,
    DialogHeader,
    TextAreaFormField,
} from '@indeed/ifl-components';
import GettextFactory from '../../../util/GettextFactory';

export const FREE_TEXT_PREFERENCE_MAX_LENGTH = 500;

export interface FreeTextPreferenceEditDialogProps {
    /** Initial text of the preference (from `freeTextPreferences.text`). */
    defaultText: string;
    /**
     * Saves the edited text. The dialog stays locked while the promise is pending
     * and unlocks again if it rejects; on success the parent dismisses the modal.
     */
    onSubmit: (text: string) => Promise<void>;
    /**
     * Render the dialog full screen. Used when presented inside a native JSMABridge
     * webview (see `FreeTextPreferenceEditPage`).
     */
    fullScreen?: boolean;
    /** Hide the web title when the native bottom-sheet header already shows it. */
    inNativeBottomSheet?: boolean;
}

/**
 * Edits the job seeker's free-text match preference.
 *
 * Uses the `alertdialog` type so the dialog itself can't be dismissed (no close
 * button, Escape, or backdrop click); dismissal is owned by the native bottom
 * sheet that hosts the page.
 *
 * Figma: https://www.figma.com/design/dWz1Ao3NwPCSdhrsiG6Yvq/Review-before-submit-2026?node-id=899-47703
 *
 * JIRA: IAFE-3513
 */
const FreeTextPreferenceEditDialog: React.FC<FreeTextPreferenceEditDialogProps> = ({
    defaultText,
    onSubmit,
    fullScreen = false,
    inNativeBottomSheet = false,
}) => {
    const gt = GettextFactory();
    const [text, setText] = useState(defaultText);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const title = gt.gettext('Match preferences');
    const isOverLimit = text.length > FREE_TEXT_PREFERENCE_MAX_LENGTH;
    const isBlank = text.trim().length === 0;
    // Invalid (blank or over the limit): red border and red counter, Update disabled.
    const isValid = !isBlank && !isOverLimit;

    const handleSubmit = async () => {
        if (!isValid || isSubmitting) return;
        setIsSubmitting(true);
        try {
            await onSubmit(text);
        } catch {
            setIsSubmitting(false);
        }
    };

    return (
        <Dialog open type="alertdialog" size="sm" mobile="auto" fullScreen={fullScreen}>
            <DialogContent aria-label={inNativeBottomSheet ? title : undefined}>
                {!inNativeBottomSheet && <DialogHeader>{title}</DialogHeader>}
                <DialogBody>
                    <TextAreaFormField
                        helperText={gt.gettext(
                            "Cover what you're looking for in your next job like role or pay, and anything you want to avoid.",
                        )}
                        disabled={isSubmitting}
                        invalid={!isValid}
                        counter={{
                            limit: FREE_TEXT_PREFERENCE_MAX_LENGTH,
                            value: text.length,
                            formatter: (value, limit) =>
                                gt.gettext('{0} / {1} characters', [value, limit]),
                            ...(!isValid && { variant: 'alert' as const }),
                            // CharacterCounter renders an inline <span>; make it a block so it can right-align.
                            sx: { display: 'block', textAlign: 'end' },
                        }}
                        textareaProps={{
                            value: text,
                            onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) =>
                                setText(e.target.value),
                            rows: 6,
                            'aria-label': title,
                        }}
                    />
                    {/* In the body rather than DialogFooter so it sits under the text area
                        (per design) instead of pinning to the bottom of the full-screen sheet. */}
                    <Box sx={{ marginBlockStart: 4 }}>
                        <Button
                            variant="primary"
                            size="lg"
                            full
                            onClick={() => void handleSubmit()}
                            disabled={!isValid || isSubmitting}
                            loading={isSubmitting}
                            spinnerProps={{ title: gt.gettext('Updating...') }}
                        >
                            {gt.gettext('Update')}
                        </Button>
                    </Box>
                </DialogBody>
            </DialogContent>
        </Dialog>
    );
};

export default FreeTextPreferenceEditDialog;
